import { useLang } from 'hooks/lang';

import { actions, invoiceControlConfigByCode, outsourceInvoiceStatusConfig } from 'entities';

import { Alert, Button, LabeledTextarea, LabeledInput, Column, Row } from 'components/Blocks';
import { CircleXIcon, FileCheckIcon } from 'components/Icons';
import { MonetaryInput, MultipleEmailsInput } from 'components/Inputs';

import { NoProjectInvoiceInfo } from './CantStart';

import { useFormControl } from 'hooks';

import { InvoiceTabProps } from '../types';

export function RegisterInvoice({
  invoice,
  overwrite,
  changeTab,
  closeModal,
}: InvoiceTabProps): JSX.Element {
  const { t } = useLang();

  const outsourceService = invoice.project.services.find(
    ({ id }) => id === invoice.serviceConfigId,
  );

  const { state, handleChange, dispatch } = useFormControl({
    companyName: '',
    taxId: '',
    address: '',
    paymentTerms: '',
    currency: outsourceService?.outsourceConfig?.providerCost.currency || '',
    value: outsourceService?.outsourceConfig?.providerCost.value || '',
    comments: '',
    emails: [] as string[],
  });

  const numericValue = Number(state.value);
  const numericServiceValue = Number(outsourceService?.outsourceConfig?.providerCost.value);

  const valueBiggerThanService = numericValue > numericServiceValue;

  const uploadBlock = [
    valueBiggerThanService,
    !Object.values(state).every(Boolean),
    !state.value.length,
  ].some(Boolean);

  const registerInvoice = actions.invoice.registerOutsourceInvoice.use({
    block: uploadBlock,

    params: {
      id: invoice.id,
      ...state,
    },

    onSuccess: () => {
      overwrite((old) => ({
        ...old,
        ...state,
        status: outsourceInvoiceStatusConfig.registered.code,

        providerCost: {
          currency: state.currency,
          ...old.providerCost,
          value: state.value,
        },
      }));

      changeTab('data');
    },
  });

  if (!invoiceControlConfigByCode[invoice.project.invoice.status].canStartOutsourceFlow)
    return <NoProjectInvoiceInfo />;

  return (
    <Column gap="8">
      <h6 className="text-center font-semibold">{t('confirm-values')}</h6>

      <Column gap="6">
        <LabeledInput
          label={t('company-name')}
          value={state.companyName}
          onChange={handleChange}
          name="companyName"
        />

        <LabeledInput
          label={t('tax-id')}
          value={state.taxId}
          onChange={handleChange}
          name="taxId"
        />

        <LabeledTextarea
          label={t('address') as string}
          value={state.address}
          onChange={handleChange}
          name="address"
        />

        <LabeledTextarea
          label={t('payment-terms') as string}
          value={state.paymentTerms}
          onChange={handleChange}
          name="paymentTerms"
        />

        <MonetaryInput
          label={t('value-approved') as string}
          onChange={handleChange}
          error={valueBiggerThanService}
          name="value"
          value={state.value}
          currency={state.currency}
          currencyDisabled
        />

        <MultipleEmailsInput
          emails={state.emails}
          updateEmails={(emails) => dispatch({ emails })}
        />

        <LabeledTextarea
          label={t('comments') as string}
          value={state.comments}
          onChange={handleChange}
          name="comments"
        />
      </Column>

      {valueBiggerThanService ? (
        <Alert level="warning" title={t('value-bigger-than-project-ref')}>
          {t('this-value-would-cause-our-profit-reference-to-decrease')}
        </Alert>
      ) : (
        <Row justifyContent="space-between">
          <Button variant="outlined" onClick={closeModal}>
            <CircleXIcon />
            {t('cancel')}
          </Button>

          <Button disabled={uploadBlock} {...registerInvoice.buttonProps}>
            {t('confirm')}
            <FileCheckIcon />
          </Button>
        </Row>
      )}
    </Column>
  );
}
